import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Database,
  Cloud, Cpu, Activity, Wifi, Save, Bell, BellOff, Trash2, Loader2, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || '';
const REFRESH_MS = 60000; // refresco automático 60s

function authHeaders() {
  const t = localStorage.getItem('lexduo_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtMs(v) {
  if (v == null) return '—';
  if (v < 1000) return `${Math.round(v)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}
function fmtMb(v) { return v == null ? '—' : `${Math.round(v)} MB`; }
function fmtGb(v) { return v == null ? '—' : `${v.toFixed(1)} GB`; }
function fmtPct(v) { return v == null ? '—' : `${v.toFixed(1)}%`; }
function fmtUptime(s) {
  if (!s) return '—';
  const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`;
}

function nivelBadge(nivel) {
  if (nivel === 'crit') return <Badge className="bg-red-600 text-white" data-testid="nivel-crit">🔴 CRÍTICO</Badge>;
  if (nivel === 'warn') return <Badge className="bg-amber-500 text-white" data-testid="nivel-warn">⚠️ AVISO</Badge>;
  return <Badge className="bg-emerald-600 text-white" data-testid="nivel-ok">✅ OK</Badge>;
}

// Mini-badge cualitativo por métrica
function evalChip(ev) {
  if (!ev) return null;
  const map = {
    excelente: { bg: 'bg-emerald-100', txt: 'text-emerald-700', label: 'Excelente' },
    normal:    { bg: 'bg-sky-100',     txt: 'text-sky-700',     label: 'Normal' },
    warn:      { bg: 'bg-amber-100',   txt: 'text-amber-800',   label: 'Lento' },
    crit:      { bg: 'bg-red-100',     txt: 'text-red-800',     label: 'Crítico' },
  };
  const s = map[ev.nivel] || map.normal;
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded ${s.bg} ${s.txt} font-medium whitespace-nowrap`}
      title={`${ev.texto}\nReferencia: ${ev.rango_ok}`}
    >
      {s.label}
    </span>
  );
}

function MetricRow({ label, field, snapshot, unit = '' }) {
  const value = snapshot?.[field];
  const ev = snapshot?.evaluaciones?.[field];
  let cls = 'text-gray-700';
  if (ev) {
    if (ev.nivel === 'crit') cls = 'text-red-600 font-bold';
    else if (ev.nivel === 'warn') cls = 'text-amber-600 font-semibold';
    else if (ev.nivel === 'excelente') cls = 'text-emerald-700 font-medium';
    else cls = 'text-sky-700';
  }
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-500 flex-1 truncate" title={ev?.texto || ''}>{label}</span>
      <div className="flex items-center gap-1.5">
        {evalChip(ev)}
        <span className={`${cls} tabular-nums min-w-[60px] text-right`}>
          {value != null ? `${value}${unit}` : '—'}
        </span>
      </div>
    </div>
  );
}

export default function ConfiguracionDiagnostico() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [hours, setHours] = useState(24);
  const [config, setConfig] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('lexduo_user') || '{}');
      setIsAdmin(u.rol === 'admin' || u.email === 'admin@lexduo.cat');
    } catch (e) { /* noop */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const [hr, sr] = await Promise.all([
        axios.get(`${API}/api/diagnostico/snapshots?hours=${hours}&limit=500`, { headers: authHeaders() }),
        axios.get(`${API}/api/diagnostico/stats?hours=${hours}`, { headers: authHeaders() }),
      ]);
      setHistory((hr.data?.snapshots || []).slice().reverse());
      setStats(sr.data);
    } catch (e) {
      console.warn('history error', e);
    }
  }, [hours]);

  const loadLast = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/diagnostico/last`, { headers: authHeaders() });
      if (r.data?.snapshot) setSnapshot(r.data.snapshot);
    } catch (e) { /* noop */ }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/diagnostico/config`, { headers: authHeaders() });
      setConfig(r.data);
    } catch (e) { /* noop */ }
  }, []);

  const runTest = async (save = true) => {
    if (running) return;
    setRunning(true);
    try {
      const url = save ? '/api/diagnostico/snapshot' : '/api/diagnostico/realtime';
      const r = save
        ? await axios.post(`${API}${url}`, {}, { headers: authHeaders() })
        : await axios.get(`${API}${url}`, { headers: authHeaders() });
      setSnapshot(r.data);
      if (save) await loadHistory();
      if (r.data?.alertas?.length) {
        toast({ title: `Diagnóstico ${r.data.nivel?.toUpperCase()}`, description: r.data.alertas.join(' | ') });
      } else {
        toast({ title: 'Diagnóstico OK', description: `Completado en ${fmtMs(r.data?.duration_ms)}` });
      }
    } catch (e) {
      toast({ title: 'Error al ejecutar test', description: e?.response?.data?.detail || 'Fallo desconocido', variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const r = await axios.put(`${API}/api/diagnostico/config`, {
        alertas_email_enabled: config.alertas_email_enabled,
        alertas_email_to: config.alertas_email_to,
        umbrales: config.umbrales,
        alerta_muestras_consecutivas: config.alerta_muestras_consecutivas,
      }, { headers: authHeaders() });
      setConfig(r.data);
      toast({ title: 'Configuración guardada' });
    } catch (e) {
      toast({ title: 'Error al guardar', description: e?.response?.data?.detail || 'Fallo', variant: 'destructive' });
    } finally {
      setSavingCfg(false);
    }
  };

  const purge = async () => {
    if (!window.confirm('¿Eliminar snapshots de más de 7 días?')) return;
    try {
      const r = await axios.delete(`${API}/api/diagnostico/cleanup?days=7`, { headers: authHeaders() });
      toast({ title: 'Limpieza completada', description: `${r.data.deleted} filas eliminadas` });
      await loadHistory();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo limpiar', variant: 'destructive' });
    }
  };

  // Auto-refresh + carga inicial
  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    Promise.all([loadLast(), loadHistory(), loadConfig()]).finally(() => setLoading(false));
    const id = setInterval(() => loadLast().then(loadHistory), REFRESH_MS);
    return () => clearInterval(id);
  }, [isAdmin, loadLast, loadHistory, loadConfig]);

  const chartData = useMemo(() => history.map(s => ({
    t: new Date(s.timestamp + 'Z').toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
    mysql: s.mysql_ping_ms, s3: s.s3_get_ms, cpu: s.cpu_percent, ram: s.ram_used_percent
  })), [history]);

  if (!isAdmin) {
    return (
      <div className="container max-w-4xl mx-auto p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Solo administradores pueden acceder al panel de diagnóstico.</AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/configuracion')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-5" data-testid="page-diagnostico">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/configuracion')} className="mb-1 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver a Configuración
          </Button>
          <h1 className="text-2xl font-bold text-gray-800">Diagnóstico del sistema</h1>
          <p className="text-sm text-gray-500">Mide latencia de MySQL, S3, CPU, RAM, red y backend. Refresco automático cada 60s.</p>
        </div>
        <div className="flex items-center gap-2">
          {snapshot && nivelBadge(snapshot.nivel)}
          <Button onClick={() => runTest(true)} disabled={running} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-run-test">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Ejecutar test ahora
          </Button>
        </div>
      </div>

      {snapshot?.alertas?.length > 0 && (
        <Alert variant={snapshot.nivel === 'crit' ? 'destructive' : 'default'} className={snapshot.nivel === 'warn' ? 'bg-amber-50 border-amber-300' : ''}>
          {snapshot.nivel === 'crit' ? <XCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <AlertDescription>
            <strong>Anomalías detectadas:</strong>
            <ul className="list-disc list-inside mt-1 text-sm">
              {snapshot.alertas.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Leyenda */}
      <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap" data-testid="legend">
        <span>Pasa el cursor sobre cada métrica para ver la explicación. Etiquetas:</span>
        <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">Excelente</span>
        <span className="bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium">Normal</span>
        <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-medium">Lento</span>
        <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-medium">Crítico</span>
      </div>

      {/* 4 cards principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="card-mysql">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Database className="h-4 w-4 text-blue-600" /> MySQL</CardTitle>
            <CardDescription className="text-xs">{snapshot ? new Date(snapshot.timestamp + 'Z').toLocaleString('es-ES') : '—'}</CardDescription>
          </CardHeader>
          <CardContent>
            <MetricRow label="Ping (SELECT 1)" field="mysql_ping_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="Query típica" field="mysql_query_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="Conexiones" field="mysql_connections" snapshot={snapshot} />
            <MetricRow label="Slow queries (total)" field="mysql_slow_queries" snapshot={snapshot} />
          </CardContent>
        </Card>

        <Card data-testid="card-s3">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Cloud className="h-4 w-4 text-orange-600" /> AWS S3</CardTitle>
            <CardDescription className="text-xs">{snapshot?.s3_error ? <span className="text-red-600">Error: {snapshot.s3_error.slice(0, 50)}...</span> : 'Sin errores'}</CardDescription>
          </CardHeader>
          <CardContent>
            <MetricRow label="HEAD" field="s3_head_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="GET" field="s3_get_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="PUT" field="s3_put_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="DELETE" field="s3_delete_ms" snapshot={snapshot} unit=" ms" />
          </CardContent>
        </Card>

        <Card data-testid="card-servidor">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Cpu className="h-4 w-4 text-emerald-600" /> Servidor</CardTitle>
            <CardDescription className="text-xs">Uptime: {fmtUptime(snapshot?.uptime_seconds)}</CardDescription>
          </CardHeader>
          <CardContent>
            <MetricRow label="CPU" field="cpu_percent" snapshot={snapshot} unit=" %" />
            <MetricRow label="RAM usada" field="ram_used_percent" snapshot={snapshot} unit=" %" />
            <MetricRow label="RAM libre" field="ram_free_mb" snapshot={snapshot} unit=" MB" />
            <MetricRow label="Swap usado" field="swap_used_mb" snapshot={snapshot} unit=" MB" />
            <MetricRow label="Load 1m" field="load_1m" snapshot={snapshot} />
            <MetricRow label="Disco usado" field="disk_used_percent" snapshot={snapshot} unit=" %" />
            <MetricRow label="Disco libre" field="disk_free_gb" snapshot={snapshot} unit=" GB" />
          </CardContent>
        </Card>

        <Card data-testid="card-red-backend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Wifi className="h-4 w-4 text-purple-600" /> Red &amp; Backend</CardTitle>
            <CardDescription className="text-xs">FastAPI + conectividad</CardDescription>
          </CardHeader>
          <CardContent>
            <MetricRow label="Ping 8.8.8.8" field="net_ping_google_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="Ping S3" field="net_ping_s3_ms" snapshot={snapshot} unit=" ms" />
            <MetricRow label="Procesos uvicorn" field="uvicorn_processes" snapshot={snapshot} />
            <MetricRow label="RSS workers" field="backend_workers_rss_mb" snapshot={snapshot} unit=" MB" />
            <MetricRow label="Conexiones :8001" field="backend_open_connections" snapshot={snapshot} />
            <div className="flex items-center justify-between text-sm py-1.5">
              <span className="text-gray-500">Duración test</span>
              <span className="text-gray-700 tabular-nums">{snapshot?.duration_ms != null ? `${snapshot.duration_ms} ms` : '—'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trend">
        <TabsList>
          <TabsTrigger value="trend" data-testid="tab-trend"><Activity className="h-4 w-4 mr-1" /> Tendencia</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><AlertTriangle className="h-4 w-4 mr-1" /> Alertas históricas</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Settings className="h-4 w-4 mr-1" /> Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="trend" className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {[6, 12, 24, 72, 168].map(h => (
                <Button key={h} size="sm" variant={hours === h ? 'default' : 'outline'} onClick={() => setHours(h)} data-testid={`btn-hours-${h}`}>
                  {h < 24 ? `${h}h` : `${h / 24}d`}
                </Button>
              ))}
            </div>
            {stats && (
              <div className="text-xs text-gray-500 flex gap-3">
                <span>📊 {stats.total_snapshots} snapshots</span>
                <span className="text-emerald-700">✅ {stats.niveles.ok}</span>
                <span className="text-amber-600">⚠️ {stats.niveles.warn}</span>
                <span className="text-red-600">🔴 {stats.niveles.crit}</span>
              </div>
            )}
          </div>

          <Card>
            <CardContent className="pt-6">
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="t" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis yAxisId="ms" tick={{ fontSize: 10 }} label={{ value: 'ms', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                    <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 10 }} label={{ value: '%', angle: 90, position: 'insideRight', fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="ms" type="monotone" dataKey="mysql" stroke="#2563eb" dot={false} name="MySQL ping (ms)" />
                    <Line yAxisId="ms" type="monotone" dataKey="s3" stroke="#ea580c" dot={false} name="S3 GET (ms)" />
                    <Line yAxisId="pct" type="monotone" dataKey="cpu" stroke="#059669" dot={false} name="CPU %" />
                    <Line yAxisId="pct" type="monotone" dataKey="ram" stroke="#7c3aed" dot={false} name="RAM %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs text-gray-600">
                  <div><strong>MySQL avg:</strong> {stats.avg.mysql_ping_ms} ms <span className="text-gray-400">(max {stats.max.mysql_ping_ms})</span></div>
                  <div><strong>S3 GET avg:</strong> {stats.avg.s3_get_ms} ms <span className="text-gray-400">(max {stats.max.s3_get_ms})</span></div>
                  <div><strong>CPU avg:</strong> {stats.avg.cpu_percent}% <span className="text-gray-400">(max {stats.max.cpu_percent})</span></div>
                  <div><strong>RAM avg:</strong> {stats.avg.ram_used_percent}%</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                Snapshots con anomalías (últimas {hours}h)
                <Button variant="outline" size="sm" onClick={purge} data-testid="btn-purge">
                  <Trash2 className="h-3 w-3 mr-1" /> Limpiar &gt;7 días
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>}
              {!loading && history.filter(s => s.nivel !== 'ok').length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6 flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  No hay anomalías en las últimas {hours} horas.
                </div>
              )}
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {history.filter(s => s.nivel !== 'ok').slice().reverse().map(s => {
                  let alerts = [];
                  try { alerts = (JSON.parse(s.notes || '{}').alertas) || []; } catch (e) { /* noop */ }
                  return (
                    <div key={s.id} className="border-l-4 px-3 py-2 text-sm bg-gray-50"
                         style={{ borderColor: s.nivel === 'crit' ? '#dc2626' : '#f59e0b' }}>
                      <div className="flex items-center gap-2 mb-1">
                        {nivelBadge(s.nivel)}
                        <span className="text-xs text-gray-500">{new Date(s.timestamp + 'Z').toLocaleString('es-ES')}</span>
                        {s.alert_sent && <Badge variant="outline" className="text-[10px]"><Bell className="h-2.5 w-2.5 mr-1" /> Email enviado</Badge>}
                      </div>
                      <ul className="text-xs list-disc list-inside text-gray-700">
                        {alerts.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config">
          {!config && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>}
          {config && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Alertas y umbrales</CardTitle>
                <CardDescription>Configura el destinatario de alertas y los umbrales que disparan aviso.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  {config.alertas_email_enabled ? <Bell className="h-4 w-4 text-blue-600" /> : <BellOff className="h-4 w-4 text-gray-400" />}
                  <Label className="flex-1">Alertas por email cuando se detecten anomalías</Label>
                  <Switch
                    checked={!!config.alertas_email_enabled}
                    onCheckedChange={v => setConfig({ ...config, alertas_email_enabled: v })}
                    data-testid="switch-alertas-email"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Destinatario alertas</Label>
                  <Input
                    type="email"
                    value={config.alertas_email_to || ''}
                    onChange={e => setConfig({ ...config, alertas_email_to: e.target.value })}
                    placeholder="admin@lexduo.cat"
                    data-testid="input-email-alertas"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Muestras consecutivas para alertar</Label>
                  <Input
                    type="number" min={1} max={10}
                    value={config.alerta_muestras_consecutivas || 2}
                    onChange={e => setConfig({ ...config, alerta_muestras_consecutivas: parseInt(e.target.value) || 2 })}
                    data-testid="input-muestras"
                  />
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Umbrales</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(config.umbrales || {}).map(([k, v]) => (
                      <div key={k}>
                        <Label className="text-xs text-gray-500">{k}</Label>
                        <Input
                          type="number"
                          value={v}
                          onChange={e => setConfig({
                            ...config,
                            umbrales: { ...config.umbrales, [k]: parseFloat(e.target.value) || 0 }
                          })}
                          data-testid={`input-umbral-${k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveConfig} disabled={savingCfg} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-config">
                    {savingCfg ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Guardar configuración
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
